import { forwardRef, useState } from 'react';

import { cx } from 'cva';
import { TextArea as RACTextArea } from 'react-aria-components/TextArea';
import {
  TextField as RACTextField,
  type TextFieldProps as RACTextFieldProps,
  type ValidationResult,
} from 'react-aria-components/TextField';

import { FieldDescription } from '../form/field_description';
import { FieldError } from '../form/field_error';
import { FieldLabel } from '../form/field_label';

import styles from './text_area_field.module.css';

export type TextAreaFieldProps = RACTextFieldProps & {
  className?: string;
  defaultValue?: string;
  description?: string;
  errorMessage?: string | ((validation: ValidationResult) => string);
  isInvalid?: boolean;
  isRequired?: boolean;
  label?: string;
  maxLength?: number;
  placeholder?: string;
  rows?: number;
};

/**
 * Text area field component that can be used to input multiline text.
 *
 * See `TextField` component for single line text input.
 *
 * See https://react-spectrum.adobe.com/react-aria/TextField.html for the underlying
 * implementation.
 *
 * ```tsx
 * <TextAreaField
 *   label="Label"
 *   isRequired
 *   placeholder="Placeholder"
 *   rows={4}
 *   maxLength={100}
 * />
 * ```
 */
export const TextAreaField = forwardRef<HTMLDivElement, TextAreaFieldProps>(
  (
    {
      className,
      defaultValue,
      description,
      errorMessage,
      isReadOnly,
      isRequired,
      label,
      maxLength,
      rows,
      ...rest
    },
    ref,
  ) => {
    const [value, setValue] = useState('');
    const maxLengthValue = maxLength || 0;
    const fillPercentage =
      maxLengthValue > 0 ? (value.length / maxLengthValue) * 100 : 0;
    const fillPercentageThreshold = 75;
    const showCharacterCount = fillPercentage > fillPercentageThreshold;

    const classes = cx(
      styles.text_area_field,
      {
        [styles.read_only]: isReadOnly,
      },
      className,
    );

    return (
      <RACTextField
        ref={ref}
        defaultValue={defaultValue}
        isRequired={isRequired}
        maxLength={maxLength}
        className={classes}
        isReadOnly={isReadOnly}
        {...rest}
      >
        {label && <FieldLabel isRequired={isRequired}>{label}</FieldLabel>}
        <div className={styles.text_area_wrapper}>
          <RACTextArea
            rows={rows}
            data-fixed-rows={rows !== undefined ? true : undefined}
            onChange={(event) => setValue(event.target.value)}
          />
          {maxLengthValue > 0 && (
            <div className={styles.character_count}>
              {showCharacterCount && `${value.length}/${maxLengthValue}`}
            </div>
          )}
        </div>
        {description && <FieldDescription>{description}</FieldDescription>}
        <FieldError>{errorMessage}</FieldError>
      </RACTextField>
    );
  },
);
